import { log, toHexString } from './util/LogUtil'
import { decodeCommon, encodeCommon, getContent, processSMPPacket } from './util/DataParser'
import { HidSender } from './util/HidSender'
// import { readFileAsync } from './util/FileUtil'
import { concatTypedArray, memcmpJS, sha256 } from './util/CommonUtil'

// Opcodes
const MGMT_OP_READ = 0
// const MGMT_OP_READ_RSP = 1
const MGMT_OP_WRITE = 2
// const MGMT_OP_WRITE_RSP = 3

// Groups
const MGMT_GROUP_ID_OS = 0
const MGMT_GROUP_ID_IMAGE = 1
// const MGMT_GROUP_ID_STAT = 2
// const MGMT_GROUP_ID_CONFIG = 3
// const MGMT_GROUP_ID_LOG = 4
// const MGMT_GROUP_ID_CRASH = 5
// const MGMT_GROUP_ID_SPLIT = 6
// const MGMT_GROUP_ID_RUN = 7
// const MGMT_GROUP_ID_FS = 8
// const MGMT_GROUP_ID_SHELL = 9

// OS group
// const OS_MGMT_ID_ECHO = 0
// const OS_MGMT_ID_CONS_ECHO_CTRL = 1
// const OS_MGMT_ID_TASKSTAT = 2
// const OS_MGMT_ID_MPSTAT = 3
// const OS_MGMT_ID_DATETIME_STR = 4
const OS_MGMT_ID_RESET = 5

// Image group
const IMG_MGMT_ID_STATE = 0
const IMG_MGMT_ID_UPLOAD = 1
// const IMG_MGMT_ID_FILE = 2
// const IMG_MGMT_ID_CORELIST = 3
// const IMG_MGMT_ID_CORELOAD = 4
// const IMG_MGMT_ID_ERASE = 5

const SMP_VERSION_2_HEADER = 0x08
// const SMP_VERSION_1_HEADER = 0x00

const image_tlv_magic: number[] = [0x07, 0x69]
const image_tlv_magic_reverse_endian: number[] = [0x69, 0x07]
const image_tlv_tag_sha256 = 0x10 & 0xffff
const image_tlv_tag_sha384 = 0x11 & 0xffff
const image_tlv_tag_sha512 = 0x12 & 0xffff
const sha256_size = 32 & 0xff
const sha384_size = 48 & 0xff
const sha512_size = 64 & 0xff
const image_tlv_magic_size = 2 & 0xff
const image_tlv_legnth_offset_1 = 2 & 0xff
const image_tlv_legnth_offset_2 = 3 & 0xff
const image_tlv_header_size = 4 & 0xff
// const image_tlv_data_header_size = 4 & 0xff

//MCUboot header constants
const ih_magic_none: number[] = [0xff, 0xff, 0xff, 0xff]
const ih_magic_v1: number[] = [0x96, 0xf3, 0xb8, 0x3c]
const ih_magic_v2: number[] = [0x96, 0xf3, 0xb8, 0x3d]
const ih_hdr_size_offs = 8
const ih_protected_tlv_size_offs = 10
const ih_img_size_offs = 12

const ENDIAN_BIG = 0
const ENDIAN_LITTLE = 1
const ENDIAN_UNKNOWN = 2

export const MODE_IDLE = 0
export const MODE_UPLOAD_FIRMWARE = 1
export const MODE_LIST_IMAGES = 2
export const MODE_SET_IMAGE = 3
export const MODE_ERASE_IMAGE = 4
export const MODE_SLOT_INFO = 5
export const MODE_RESET = 6

const smp_mtu = 256

export interface SlotInfo {
  slot: number
  active: boolean
  [key: string]: unknown
}

export class NordicLFirmwareUpgrade {
  device: HIDDevice | null = null
  mode: number = MODE_IDLE
  m_rec_data: number[] = []
  curImage: SlotInfo | null = null
  updateImage: SlotInfo | null = null
  fwUint8Array: Uint8Array | null = null
  endian = ENDIAN_UNKNOWN
  upload_endian = ENDIAN_UNKNOWN
  file_upload_area = 0
  upload_image = 0
  upgrade_only = false
  hash: Uint8Array | null = null
  _seq = 0
  updateProgressCallback: ((progress: number) => void) | null = null
  updateFinishedCallback: (() => void) | null = null
  slotInfoCallback: ((curSlotInfo: SlotInfo) => void) | null = null
  smpErrorCallback: ((error: string) => void) | null = null

  debugOffPre = 0
  debugOff = 0

  data_max_sizePre = 0
  data_max_size = 0
  startTime: number | null = null
  sender: HidSender | null = null

  setUpdateProgressCallback(callback: (progress: number) => void): void {
    this.updateProgressCallback = callback
  }

  setUpdateFinishedCallback(callback: () => void): void {
    this.updateFinishedCallback = callback
  }

  setSlotInfoCallback(callback: (curSlotInfo: SlotInfo) => void): void {
    this.slotInfoCallback = callback
  }

  setSmpErrorCallback(callback: (error: string) => void): void {
    this.smpErrorCallback = callback
  }

  handleInputReport(event: HIDInputReportEvent): void {
    if (event.reportId !== 0x0b) return

    const dataItem = new Uint8Array(event.data.buffer)
    const length = dataItem[0]

    for (let a = 0; a < length; a++) {
      const dataByte = dataItem[1 + a]
      this.m_rec_data.push(dataByte)
      if (dataByte === 0x0a) {
        processSMPPacket(
          new Uint8Array(this.m_rec_data),
          this.dataReceivedCallback.bind(this),
          this.smpErrorCallback ?? undefined,
        )
        this.m_rec_data = []
      }
    }
  }

  async dataReceivedCallback(smpData: Uint8Array): Promise<void> {
    if (this.mode === MODE_LIST_IMAGES) {
      this.imageInfo(smpData)
      return
    }
    if (this.mode === MODE_UPLOAD_FIRMWARE) {
      const contentUpdate = getContent(smpData)
      const decodeUpdate = decodeCommon(contentUpdate!)
      await this.fileUpload(decodeUpdate as { off: number })
      return
    }
    if (this.mode === MODE_SET_IMAGE) {
      await this.start_reset()
      return
    }
  }

  emitSendUpdateProgress(progress: number): void {
    if (this.updateProgressCallback) {
      this.updateProgressCallback(progress)
    }
  }

  emitUpdateFinished(): void {
    if (this.updateFinishedCallback) {
      this.updateFinishedCallback()
    }
  }

  max_message_data_size(mtu: number): number {
    let available_mtu = mtu
    const packets = Math.ceil(available_mtu / 124.0)

    //Convert to number of base64 encoded bytes
    available_mtu = (available_mtu * 3.0) / 4.0

    //Remove packet length and CRC (2 bytes each)
    available_mtu -= 4.0

    //Remove header and footer of each packet
    available_mtu -= packets * 3.0

    //Remove possible padding bytes for narrow final packets
    if (available_mtu % 93 >= 91) {
      available_mtu -= 3.0
    } else if (available_mtu % 93 >= 88) {
      available_mtu -= 1.0
    }

    return available_mtu
  }

  imageInfo(smpData: Uint8Array): void {
    const content = getContent(smpData)
    const imageState = decodeCommon(content!) as { images: SlotInfo[] }
    // log.info(`imageState: ${JSON.stringify(imageState)}`)

    for (const image of imageState.images) {
      if (image.active) {
        //当前正在使用的Slot
        this.curImage = image
      } else {
        //要升级的Slot，TODO如果升级不完整，可能获取不到，所以不能用这个判断
        this.updateImage = image
      }
    }
    if (this.slotInfoCallback != null && this.curImage != null) {
      this.slotInfoCallback(this.curImage)
    }
  }

  async updateFwSlot(): Promise<string | null> {
    this.startTime = Date.now()

    if (!this.fwUint8Array || this.fwUint8Array.length < ih_magic_none.length) {
      log.info(`固件不完整`)
      return `固件不完整`
    }

    this.setEndian()
    if (this.endian === ENDIAN_UNKNOWN) {
      log.info(`固件不完整`)
      return `固件不完整`
    }

    const fwHash = this.getFwHash()
    this.hash = fwHash
    if (!fwHash) {
      log.info(`固件不完整`)
      return `固件不完整`
    }

    this.mode = MODE_UPLOAD_FIRMWARE
    this.upload_image = 0
    this.file_upload_area = 0
    this.upgrade_only = false
    await this.fileUpload()
    return null
  }

  async fileUpload(cborOff?: { off: number } | null): Promise<void> {
    if (cborOff != null) {
      this.debugOffPre = this.debugOff
      this.debugOff = cborOff.off

      //TODO 后续做重试
      if (cborOff.off !== -1) {
        this.file_upload_area = cborOff.off
      }

      if (this.file_upload_area !== 0 && this.fwUint8Array) {
        this.emitSendUpdateProgress((this.file_upload_area * 100) / this.fwUint8Array.length)
      }
    }
    //Upload next chunk
    const max_size = this.max_message_data_size(smp_mtu)

    const cborDataTemp: Record<string, unknown> = {}
    if (this.fwUint8Array && this.file_upload_area >= this.fwUint8Array.length) {
      //上传完了
      this.mode = MODE_IDLE
      this.upload_image = 0
      this.fwUint8Array = null
      this.file_upload_area = 0
      this.upgrade_only = false

      this.emitSendUpdateProgress(100)
      this.emitUpdateFinished()
      return
    }

    if (!this.fwUint8Array) {
      return
    }

    //首次发送
    if (this.file_upload_area === 0) {
      if (this.upload_image !== 0) {
        cborDataTemp.image = this.upload_image
      }
      cborDataTemp.len = this.fwUint8Array.length
      cborDataTemp.sha = await sha256(this.fwUint8Array)
      if (this.upgrade_only) {
        cborDataTemp.upgrade = true
      }
    }
    cborDataTemp.off = this.file_upload_area
    // 编码临时数据以计算长度（不含 data）
    const tempCbor = encodeCommon(cborDataTemp)
    const tempCborLength = tempCbor.length

    const reserved_header_bytes = 3 + 5 + 5 // 键 "data" 开销3 + 值头部5 ,TODO 还有5个字节不确认
    const available_size = max_size - 8 - tempCborLength - reserved_header_bytes

    // 计算实际可写入的 data 长度
    const data_max_size = Math.min(available_size, this.fwUint8Array.length - this.file_upload_area)

    this.data_max_sizePre = this.data_max_size
    this.data_max_size = data_max_size

    const cborData: Record<string, unknown> = {
      ...cborDataTemp,
    }
    cborData.data = this.fwUint8Array.slice(
      this.file_upload_area,
      this.file_upload_area + data_max_size,
    )

    const cborDataArr = encodeCommon(cborData)

    const _flags = 0
    const length_lo = cborDataArr.length & 255
    const length_hi = cborDataArr.length >> 8
    const group_lo = MGMT_GROUP_ID_IMAGE & 255
    const group_hi = MGMT_GROUP_ID_IMAGE >> 8

    const op = (SMP_VERSION_2_HEADER | MGMT_OP_WRITE) & 0xff
    const id = IMG_MGMT_ID_UPLOAD

    const smpHeader = new Uint8Array([
      op,
      _flags,
      length_hi,
      length_lo,
      group_hi,
      group_lo,
      this._seq,
      id,
    ])
    const message = concatTypedArray(smpHeader, cborDataArr)
    await this.send(message)
  }

  async send(smpData: Uint8Array): Promise<void> {
    await this.sender!.send(smpData, this.mode)
  }

  async deviceSetImage(): Promise<void> {
    this.mode = MODE_SET_IMAGE
    const cborData: Record<string, unknown> = {}
    cborData['hash'] = this.hash
    cborData['confirm'] = true

    const cborDataArr = encodeCommon(cborData)
    const _flags = 0
    const length_lo = cborDataArr.length & 255
    const length_hi = cborDataArr.length >> 8

    const group_lo = MGMT_GROUP_ID_IMAGE & 255
    const group_hi = MGMT_GROUP_ID_IMAGE >> 8
    const op = (SMP_VERSION_2_HEADER | MGMT_OP_WRITE) & 0xff

    const id = IMG_MGMT_ID_STATE

    const message = concatTypedArray(
      new Uint8Array([op, _flags, length_hi, length_lo, group_hi, group_lo, this._seq, id]),
      cborDataArr,
    )
    log.info(`fileUpload ${toHexString(message, ',0x')}`)
    await this.send(message)
  }

  async start_reset(): Promise<void> {
    this.mode = MODE_RESET
    const cborData: Record<string, unknown> = {}
    cborData['force'] = true

    const cborDataArr = encodeCommon(cborData)

    const _flags = 0

    const length_lo = cborDataArr.length & 255
    const length_hi = cborDataArr.length >> 8
    const group_lo = MGMT_GROUP_ID_OS & 255
    const group_hi = MGMT_GROUP_ID_OS >> 8
    const op = (SMP_VERSION_2_HEADER | MGMT_OP_WRITE) & 0xff

    const id = OS_MGMT_ID_RESET

    const message = concatTypedArray(
      new Uint8Array([op, _flags, length_hi, length_lo, group_hi, group_lo, this._seq, id]),
      cborDataArr,
    )
    log.info(`fileUpload ${toHexString(message, ',0x')}`)
    await this.send(message)
  }

  getFwHash(): Uint8Array | null {
    if (!this.fwUint8Array) return null

    let hdr_size: number
    let protected_tlv_size: number
    let img_size: number
    if (this.upload_endian !== ENDIAN_BIG) {
      hdr_size = this.fwUint8Array[ih_hdr_size_offs]
      hdr_size |= this.fwUint8Array[ih_hdr_size_offs + 1] << 8

      protected_tlv_size = this.fwUint8Array[ih_protected_tlv_size_offs]
      protected_tlv_size |= this.fwUint8Array[ih_protected_tlv_size_offs + 1] << 8

      img_size = this.fwUint8Array[ih_img_size_offs]
      img_size |= this.fwUint8Array[ih_img_size_offs + 1] << 8
      img_size |= this.fwUint8Array[ih_img_size_offs + 2] << 16
      img_size |= this.fwUint8Array[ih_img_size_offs + 3] << 24
    } else {
      hdr_size = this.fwUint8Array[ih_hdr_size_offs + 1]
      hdr_size |= this.fwUint8Array[ih_hdr_size_offs] << 8

      protected_tlv_size = this.fwUint8Array[ih_protected_tlv_size_offs + 1]
      protected_tlv_size |= this.fwUint8Array[ih_protected_tlv_size_offs] << 8

      img_size = this.fwUint8Array[ih_img_size_offs + 3]
      img_size |= this.fwUint8Array[ih_img_size_offs + 2] << 8
      img_size |= this.fwUint8Array[ih_img_size_offs + 1] << 16
      img_size |= this.fwUint8Array[ih_img_size_offs] << 24
    }

    let pos = hdr_size + protected_tlv_size + img_size
    let tlv_area_length = 0
    let found = false

    // console.log(
    //   `固件解析: hdrSize=${hdr_size}, protectedTlvSize=${protected_tlv_size}, imgSize=${img_size}, initial pos=${pos}`,
    //   this.fwUint8Array,
    // )
    while (pos + image_tlv_magic_size < this.fwUint8Array.length) {
      const fwSlice = this.fwUint8Array.slice(pos, pos + image_tlv_magic_size)
      const compare = memcmpJS(fwSlice, image_tlv_magic)
      const test1 = this.upload_endian !== ENDIAN_BIG && compare === 0
      const test2 =
        this.upload_endian === ENDIAN_BIG && memcmpJS(fwSlice, image_tlv_magic_reverse_endian) === 0
      if (test1 || test2) {
        if (this.upload_endian !== ENDIAN_BIG) {
          tlv_area_length = this.fwUint8Array[pos + image_tlv_legnth_offset_1]
          tlv_area_length |= this.fwUint8Array[pos + image_tlv_legnth_offset_2] << 8
        } else {
          tlv_area_length = this.fwUint8Array[pos + image_tlv_legnth_offset_1]
          tlv_area_length |= this.fwUint8Array[pos + image_tlv_legnth_offset_2] << 8
        }

        found = true
        break
      }

      ++pos
    }

    if (!found) {
      log.info(`没有找到tlv`)
      return null
    }
    // log.info(`tlv_area_length :${tlv_area_length}`)
    let new_pos = pos + image_tlv_header_size
    let hash: Uint8Array | null = null
    while (new_pos < pos + tlv_area_length) {
      //TODO: TLVs are > 8-bit
      const type = this.fwUint8Array[new_pos]
      let local_length: number
      if (this.upload_endian !== ENDIAN_BIG) {
        local_length = this.fwUint8Array[new_pos + image_tlv_legnth_offset_1]
        local_length |= this.fwUint8Array[new_pos + image_tlv_legnth_offset_2] << 8
      } else {
        local_length = this.fwUint8Array[new_pos + image_tlv_legnth_offset_2]
        local_length |= this.fwUint8Array[new_pos + image_tlv_legnth_offset_1] << 8
      }

      if (
        type === image_tlv_tag_sha256 ||
        type === image_tlv_tag_sha384 ||
        type === image_tlv_tag_sha512
      ) {
        const hash_size =
          type === image_tlv_tag_sha256
            ? sha256_size
            : type === image_tlv_tag_sha384
              ? sha384_size
              : sha512_size

        if (local_length === hash_size) {
          //We have the hash we wanted
          hash = this.fwUint8Array.slice(new_pos + 4, new_pos + 4 + local_length)
        }
      }

      new_pos += local_length + 4
    }
    // log.info(`hash:${toHexString(hash,  ',0x')}`)
    return hash
  }

  setEndian(): void {
    if (!this.fwUint8Array) return

    const mcuboot_magic = this.fwUint8Array.slice(0, ih_magic_none.length)

    if (memcmpJS(mcuboot_magic, ih_magic_v1) === 0 || memcmpJS(mcuboot_magic, ih_magic_v2) === 0) {
      this.endian = ENDIAN_BIG
      return
    }
    if (memcmpJS(mcuboot_magic, ih_magic_none) === 0) {
      this.endian = ENDIAN_UNKNOWN
      return
    }
    const reversed_mcuboot_magic: number[] = [
      mcuboot_magic[3],
      mcuboot_magic[2],
      mcuboot_magic[1],
      mcuboot_magic[0],
    ]
    if (
      memcmpJS(reversed_mcuboot_magic, ih_magic_v1) === 0 ||
      memcmpJS(reversed_mcuboot_magic, ih_magic_v2) === 0
    ) {
      this.endian = ENDIAN_LITTLE
      return
    }
    this.endian = ENDIAN_UNKNOWN
  }

  async start_image_get(): Promise<void> {
    this.mode = MODE_LIST_IMAGES

    const _flags = 0
    const encodedData = [0xa0]

    const length_lo = encodedData.length & 255
    const length_hi = encodedData.length >> 8
    const group_lo = MGMT_GROUP_ID_IMAGE & 255
    const group_hi = MGMT_GROUP_ID_IMAGE >> 8
    const op = (SMP_VERSION_2_HEADER | MGMT_OP_READ) & 0xff
    const id = IMG_MGMT_ID_STATE

    const message = new Uint8Array([
      op,
      _flags,
      length_hi,
      length_lo,
      group_hi,
      group_lo,
      this._seq,
      id,
      ...encodedData,
    ])
    log.info(`message:${toHexString(message)}`)
    await this.send(message)
  }

  /**
   * 发送 Feature Report 进入更新模式
   * @param payload 进入更新模式的命令字节，默认 [0xff, 0xa8, 0x01]
   */
  async enterUpdateMode(payload: number[] = [0xff, 0xa8, 0x01]): Promise<DataView | undefined> {
    const reportId = 0x06
    const sendData = new Uint8Array(payload)
    return await this.sender!.sendAndGetFeatureData(reportId, sendData)
  }

  async connect(): Promise<HIDDevice | null> {
    try {
      const devices = await navigator.hid.requestDevice({
        filters: [{ usagePage: 0xff00 }],
      })

      if (devices.length === 0) {
        return null
      }
      this.device = devices[0]
      // log.info('信息', `已连接到设备: ${this.device.productName}`)
      if (!this.device.opened) {
        await this.device.open()
        log.info('信息', `已连接到设备: ${this.device.productName}`)
      }

      this.device.addEventListener('inputreport', this.handleInputReport.bind(this))
      this.sender = new HidSender(this.device)

      return this.device
    } catch (error) {
      log.info('错误', `连接设备失败: ${(error as Error).message}`)
    }
    return null
  }

  setDevice(device: HIDDevice): void {
    this.device = device
    this.device.addEventListener('inputreport', this.handleInputReport.bind(this))
    this.sender = new HidSender(this.device)
  }

  setFwUint8Array(fwUint8Array: Uint8Array): void {
    this.fwUint8Array = fwUint8Array
    // console.log('setFwUint8Array', fwUint8Array)
  }
}
